import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  PushPin, PushPinSlash, NotePencil, Trash, Plus, X,
  MagnifyingGlass, Tag, CaretDown, CaretRight, Paperclip, UploadSimple,
} from "@phosphor-icons/react";

/**
 * Solicitor-only internal file-notes for a case. Lives directly under the
 * Conveyancing Timeline on the case page. Features: pin-to-top, colour tag,
 * attach-to-stage, attach-to-document, search, edit, soft-delete.
 *
 * Renders nothing for non-solicitors — backend will 403 anyway, this just
 * skips an unnecessary fetch.
 */
const COLORS = [
  { value: "slate",  label: "Default", swatch: "#64748b", bg: "bg-slate-50",  border: "border-slate-200",  text: "text-slate-700" },
  { value: "yellow", label: "Yellow",  swatch: "#eab308", bg: "bg-yellow-50", border: "border-yellow-200", text: "text-yellow-800" },
  { value: "red",    label: "Red",     swatch: "#ef4444", bg: "bg-red-50",    border: "border-red-200",    text: "text-red-700" },
  { value: "green",  label: "Green",   swatch: "#22c55e", bg: "bg-emerald-50",border: "border-emerald-200",text: "text-emerald-800" },
  { value: "blue",   label: "Blue",    swatch: "#3b82f6", bg: "bg-blue-50",   border: "border-blue-200",   text: "text-blue-700" },
  { value: "violet", label: "Violet",  swatch: "#8b5cf6", bg: "bg-violet-50", border: "border-violet-200", text: "text-violet-700" },
];
const colorMeta = (c) => COLORS.find((x) => x.value === c) || COLORS[0];

// Open an attached case document in a new tab (fetches the blob through the
// authenticated API so private documents work).
async function openDocument(docId) {
  try {
    const resp = await API.get(`/documents/${docId}/download`, { responseType: "blob" });
    const url = window.URL.createObjectURL(resp.data);
    window.open(url, "_blank", "noopener");
    setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  } catch {
    toast.error("Could not open document");
  }
}

function fmtWhen(s) {
  if (!s) return "";
  try {
    const d = new Date(s);
    return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return s;
  }
}

export default function FileNotesPanel({ caseId, isSolicitor, stages = [], documents = [] }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null); // note being edited (object)
  const [form, setForm] = useState({
    title: "", body: "", color: "yellow",
    stage_id: "", document_ids: [],
  });
  // Documents uploaded straight from the editor (import-from-desktop) that
  // aren't yet in the parent `documents` prop — merge so their chips resolve.
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const allDocuments = [...uploadedDocs, ...documents];
  // Per-note collapse state — keyed by note id. UI-only (not persisted).
  // ``collapseAll`` is the panel-level fallback: when set, every note
  // collapses unless its id is explicitly overridden in ``collapseMap``.
  // Defaults to collapsed so the case page stays scannable; click any
  // title or "Expand all" to open them.
  const [collapseMap, setCollapseMap] = useState({});
  const [collapseAll, setCollapseAll] = useState(true);

  const isCollapsed = (id) => {
    if (id in collapseMap) return collapseMap[id];
    return collapseAll;
  };
  const toggleOne = (id) => {
    setCollapseMap((prev) => ({ ...prev, [id]: !isCollapsed(id) }));
  };
  const setAllCollapsed = (v) => {
    setCollapseAll(v);
    setCollapseMap({}); // clear per-note overrides so the bulk action wins
  };

  const load = useCallback(async () => {
    if (!isSolicitor || !caseId) return;
    setLoading(true);
    try {
      const { data } = await API.get(`/cases/${caseId}/file-notes`);
      setNotes(data);
    } catch (err) {
      if (err.response?.status !== 403) {
        toast.error(err.response?.data?.detail || "Failed to load notes");
      }
    } finally {
      setLoading(false);
    }
  }, [caseId, isSolicitor]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => setForm({ title: "", body: "", color: "yellow", stage_id: "", document_ids: [] });

  const startCreate = () => {
    setEditing(null);
    resetForm();
    setCreating(true);
  };
  const startEdit = (n) => {
    setEditing(n);
    setForm({
      title: n.title || "",
      body: n.body || "",
      color: n.color || "slate",
      stage_id: n.stage_id || "",
      document_ids: n.document_ids?.length ? n.document_ids : (n.document_id ? [n.document_id] : []),
    });
    setCreating(true);
  };
  const cancel = () => { setCreating(false); setEditing(null); resetForm(); };

  const save = async () => {
    const body = (form.body || "").trim();
    if (!body) {
      toast.error("Note body cannot be empty");
      return;
    }
    const payload = {
      title: form.title.trim(),
      body,
      color: form.color,
      stage_id: form.stage_id || null,
      document_ids: form.document_ids || [],
    };
    try {
      if (editing) {
        const { data } = await API.put(`/file-notes/${editing.id}`, payload);
        setNotes((prev) => prev.map((n) => (n.id === data.id ? data : n)));
        toast.success("Note updated");
      } else {
        const { data } = await API.post(`/cases/${caseId}/file-notes`, payload);
        // POST returns the note; resort the list to keep pin order.
        setNotes((prev) => sortNotes([data, ...prev]));
        toast.success("Note added");
      }
      cancel();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save note");
    }
  };

  const togglePin = async (n) => {
    try {
      const { data } = await API.put(`/file-notes/${n.id}`, { pinned: !n.pinned });
      setNotes((prev) => sortNotes(prev.map((x) => (x.id === data.id ? data : x))));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update pin");
    }
  };

  const removeNote = async (n) => {
    if (!window.confirm("Delete this note? This cannot be undone.")) return;
    try {
      await API.delete(`/file-notes/${n.id}`);
      setNotes((prev) => prev.filter((x) => x.id !== n.id));
      toast.success("Note deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete note");
    }
  };

  const sortNotes = (arr) => [...arr].sort((a, b) => {
    if (!!b.pinned !== !!a.pinned) return b.pinned ? 1 : -1;
    const ak = new Date(a.pinned_at || a.created_at || 0).getTime();
    const bk = new Date(b.pinned_at || b.created_at || 0).getTime();
    return bk - ak;
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return notes;
    return notes.filter((n) =>
      (n.title || "").toLowerCase().includes(q)
      || (n.body || "").toLowerCase().includes(q)
      || (n.author_name || "").toLowerCase().includes(q)
    );
  }, [search, notes]);

  if (!isSolicitor) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm mt-6" data-testid="file-notes-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
            File Notes
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Internal notes for this case — only visible to solicitors. {notes.length} note{notes.length === 1 ? "" : "s"}.
          </p>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-56">
            <MagnifyingGlass weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search notes…"
              className="pl-8 h-8 text-xs"
              data-testid="file-notes-search"
            />
          </div>
          {!creating && (
            <>
              {notes.length > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs gap-1 border-slate-200"
                  onClick={() => setAllCollapsed(!collapseAll)}
                  data-testid="file-notes-collapse-all-btn"
                  title={collapseAll ? "Expand all notes" : "Collapse all notes — show titles only"}
                >
                  {collapseAll
                    ? <><CaretDown weight="bold" className="w-3.5 h-3.5" /> Expand all</>
                    : <><CaretRight weight="bold" className="w-3.5 h-3.5" /> Collapse all</>}
                </Button>
              )}
              <Button size="sm" className="h-8 text-xs gap-1" onClick={startCreate} data-testid="file-notes-add-btn">
                <Plus weight="bold" className="w-3.5 h-3.5" /> New note
              </Button>
            </>
          )}
        </div>
      </div>

      {creating && (
        <NoteEditor
          form={form}
          setForm={setForm}
          stages={stages}
          documents={allDocuments}
          caseId={caseId}
          onUploaded={(doc) => setUploadedDocs((p) => [doc, ...p])}
          editing={editing}
          onCancel={cancel}
          onSave={save}
        />
      )}

      {loading ? (
        <p className="text-xs text-slate-400 py-6 text-center">Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="text-xs text-slate-400 py-6 text-center" data-testid="file-notes-empty">
          {search ? `No notes match "${search}".` : "No notes yet. Click \"New note\" above."}
        </p>
      ) : (
        <ul className="space-y-2">
          {filtered.map((n) => (
            <NoteCard
              key={n.id}
              note={n}
              stages={stages}
              documents={allDocuments}
              collapsed={isCollapsed(n.id)}
              onToggleCollapse={() => toggleOne(n.id)}
              onPin={() => togglePin(n)}
              onEdit={() => startEdit(n)}
              onDelete={() => removeNote(n)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function NoteEditor({ form, setForm, stages, documents, caseId, onUploaded, editing, onCancel, onSave }) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const dragDepth = useRef(0);

  const uploadFiles = async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    setUploading(true);
    let ok = 0;
    for (const file of list) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await API.post(`/cases/${caseId}/documents?category=uncategorised`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        onUploaded?.(data);
        setForm((f) => ({ ...f, document_ids: [...f.document_ids, data.id] }));
        ok += 1;
      } catch {
        toast.error(`Upload failed: ${file.name}`);
      }
    }
    if (ok) toast.success(`${ok} file${ok === 1 ? "" : "s"} attached`);
    setUploading(false);
  };

  const handleImport = async (e) => {
    const files = e.target.files;
    e.target.value = "";
    await uploadFiles(files);
  };

  const handleDragEnter = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (e.dataTransfer?.types?.includes("Files")) {
      dragDepth.current += 1;
      setDragOver(true);
    }
  };
  const handleDragOver = (e) => {
    e.preventDefault(); e.stopPropagation();
  };
  const handleDragLeave = (e) => {
    e.preventDefault(); e.stopPropagation();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragOver(false);
  };
  const handleDrop = async (e) => {
    e.preventDefault(); e.stopPropagation();
    dragDepth.current = 0;
    setDragOver(false);
    await uploadFiles(e.dataTransfer?.files);
  };

  return (
    <div
      className={`relative bg-slate-50 border rounded-lg p-3 mb-3 space-y-2 transition-colors ${dragOver ? "border-blue-400 border-dashed ring-2 ring-blue-200" : "border-slate-200"}`}
      data-testid="file-notes-editor"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {dragOver && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-blue-50/90 border-2 border-dashed border-blue-400 pointer-events-none" data-testid="file-notes-drop-overlay">
          <div className="flex items-center gap-2 text-sm font-semibold text-blue-700">
            <UploadSimple weight="bold" className="w-5 h-5" /> Drop to attach
          </div>
        </div>
      )}
      <Input
        value={form.title}
        onChange={(e) => setForm({ ...form, title: e.target.value })}
        placeholder="Title (optional)"
        className="h-8 text-sm"
        data-testid="file-notes-title-input"
        maxLength={200}
      />
      <Textarea
        value={form.body}
        onChange={(e) => setForm({ ...form, body: e.target.value })}
        placeholder="Write your file note here…"
        className="text-sm min-h-[80px]"
        data-testid="file-notes-body-input"
      />
      <div className="flex flex-wrap items-center gap-2">
        {/* Colour picker */}
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 h-8 px-2 rounded-md border border-slate-200 text-xs text-slate-600 hover:bg-white"
              data-testid="file-notes-color-btn"
              title="Colour"
            >
              <span className="w-3 h-3 rounded-full inline-block" style={{ background: colorMeta(form.color).swatch }} />
              {colorMeta(form.color).label}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-44 p-1" align="start">
            {COLORS.map((c) => (
              <button
                key={c.value}
                type="button"
                onClick={() => setForm({ ...form, color: c.value })}
                className={`w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded hover:bg-slate-50 ${
                  form.color === c.value ? "bg-slate-100 font-semibold" : ""
                }`}
                data-testid={`file-notes-color-${c.value}`}
              >
                <span className="w-3 h-3 rounded-full inline-block" style={{ background: c.swatch }} />
                {c.label}
              </button>
            ))}
          </PopoverContent>
        </Popover>

        {/* Stage attach */}
        <Select
          value={form.stage_id || "__none__"}
          onValueChange={(v) => setForm({ ...form, stage_id: v === "__none__" ? "" : v })}
        >
          <SelectTrigger className="h-8 text-xs w-40" data-testid="file-notes-stage-select">
            <SelectValue placeholder="Attach to stage" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">No stage</SelectItem>
            {stages.map((s) => (
              <SelectItem key={s.id} value={s.id}>{s.name || s.title || "(unnamed)"}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Documents attach — multi-select */}
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 h-8 px-2 rounded-md border border-slate-200 text-xs text-slate-600 hover:bg-white"
              data-testid="file-notes-doc-select"
              title="Attach documents"
            >
              <Paperclip weight="bold" className="w-3.5 h-3.5" />
              {form.document_ids.length ? `${form.document_ids.length} document${form.document_ids.length === 1 ? "" : "s"}` : "Attach documents"}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-1 max-h-64 overflow-y-auto" align="start">
            {documents.length === 0 ? (
              <p className="text-xs text-slate-400 px-2 py-2">No documents on this case yet.</p>
            ) : documents.map((d) => {
              const on = form.document_ids.includes(d.id);
              return (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => setForm({
                    ...form,
                    document_ids: on
                      ? form.document_ids.filter((x) => x !== d.id)
                      : [...form.document_ids, d.id],
                  })}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded hover:bg-slate-50 text-left ${on ? "bg-slate-100 font-medium" : ""}`}
                  data-testid={`file-notes-doc-option-${d.id}`}
                >
                  <input type="checkbox" readOnly checked={on} className="rounded border-slate-300 shrink-0" />
                  <span className="truncate">{d.original_filename}</span>
                </button>
              );
            })}
          </PopoverContent>
        </Popover>

        {/* Import from desktop */}
        <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleImport} data-testid="file-notes-import-input" />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-1.5 h-8 px-2 rounded-md border border-slate-200 text-xs text-slate-600 hover:bg-white disabled:opacity-50"
          data-testid="file-notes-import-btn"
          title="Upload files from your computer, or drag and drop them onto the editor"
        >
          <UploadSimple weight="bold" className="w-3.5 h-3.5" />
          {uploading ? "Uploading…" : "Import from desktop"}
        </button>

        {/* Selected document chips */}
        {form.document_ids.map((id) => {
          const d = documents.find((x) => x.id === id);
          return (
            <span key={id} className="inline-flex items-center gap-1 text-[11px] bg-white border border-slate-200 rounded-full pl-2 pr-1 py-0.5 text-slate-700" data-testid={`file-notes-doc-chip-${id}`}>
              <Paperclip weight="bold" className="w-2.5 h-2.5" />
              <span className="max-w-[140px] truncate">{d?.original_filename || "Document"}</span>
              <button type="button" onClick={() => setForm({ ...form, document_ids: form.document_ids.filter((x) => x !== id) })} className="hover:text-rose-600" data-testid={`file-notes-doc-remove-${id}`}>
                <X weight="bold" className="w-3 h-3" />
              </button>
            </span>
          );
        })}

        <div className="grow" />
        <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={onCancel} data-testid="file-notes-cancel-btn">
          Cancel
        </Button>
        <Button size="sm" className="h-8 text-xs" onClick={onSave} data-testid="file-notes-save-btn">
          {editing ? "Save changes" : "Add note"}
        </Button>
      </div>
    </div>
  );
}

function NoteCard({ note, stages, documents, collapsed, onToggleCollapse, onPin, onEdit, onDelete }) {
  const meta = colorMeta(note.color);
  const stage = note.stage_id ? stages.find((s) => s.id === note.stage_id) : null;
  const attachedIds = note.document_ids?.length ? note.document_ids : (note.document_id ? [note.document_id] : []);
  const attachedDocs = attachedIds
    .map((id) => documents.find((d) => d.id === id) || { id, original_filename: "Document" });
  // Untitled notes still need a useful collapsed label — fall back to a
  // 80-char preview of the body so the user can identify the note.
  const fallbackHeading = (note.body || "").trim().split("\n")[0].slice(0, 80) + ((note.body || "").length > 80 ? "…" : "");
  const heading = note.title?.trim() || fallbackHeading || "(Untitled note)";
  return (
    <li
      className={`relative rounded-lg border ${meta.border} ${meta.bg} ${collapsed ? "py-1.5 px-3" : "p-3"}`}
      data-testid={`file-note-${note.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <button
          type="button"
          onClick={onToggleCollapse}
          className="flex items-start gap-2 min-w-0 grow text-left"
          data-testid={`file-note-toggle-${note.id}`}
          title={collapsed ? "Expand note" : "Collapse note"}
        >
          {collapsed
            ? <CaretRight weight="bold" className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${meta.text}`} />
            : <CaretDown weight="bold" className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${meta.text}`} />}
          <div className="min-w-0">
            <p className={`text-sm font-semibold ${meta.text} truncate`}>{heading}</p>
            {!collapsed && (
              <p className="text-[10px] text-slate-500">
                {note.author_name || "Unknown"} · {fmtWhen(note.created_at)}
                {note.updated_at && note.updated_at !== note.created_at && (
                  <span> · edited {fmtWhen(note.updated_at)}</span>
                )}
              </p>
            )}
          </div>
        </button>
        <div className="flex items-center gap-0.5 shrink-0">
          <button
            type="button"
            onClick={onPin}
            className={`w-7 h-7 inline-flex items-center justify-center rounded hover:bg-white/60 ${note.pinned ? meta.text : "text-slate-400"}`}
            data-testid={`file-note-pin-${note.id}`}
            title={note.pinned ? "Unpin" : "Pin to top"}
          >
            {note.pinned ? <PushPin weight="fill" className="w-4 h-4" /> : <PushPinSlash className="w-4 h-4" />}
          </button>
          <button
            type="button"
            onClick={onEdit}
            className="w-7 h-7 inline-flex items-center justify-center rounded hover:bg-white/60 text-slate-500"
            data-testid={`file-note-edit-${note.id}`}
            title="Edit"
          >
            <NotePencil weight="bold" className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="w-7 h-7 inline-flex items-center justify-center rounded hover:bg-white/60 text-rose-500"
            data-testid={`file-note-delete-${note.id}`}
            title="Delete"
          >
            <Trash weight="bold" className="w-4 h-4" />
          </button>
        </div>
      </div>
      {!collapsed && (
        <>
          <p className={`text-sm whitespace-pre-wrap break-words mt-1 ${meta.text}`}>{note.body}</p>
          {(stage || attachedDocs.length > 0) && (
            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
              {stage && (
                <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-white/70 border border-white/50 text-slate-700">
                  <Tag weight="bold" className="w-2.5 h-2.5" /> Stage: {stage.name || stage.title}
                </span>
              )}
              {attachedDocs.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => openDocument(d.id)}
                  className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-white/70 border border-white/50 text-indigo-700 hover:bg-white hover:underline"
                  data-testid={`file-note-doc-${note.id}-${d.id}`}
                  title={`Open ${d.original_filename}`}
                >
                  <Paperclip weight="bold" className="w-2.5 h-2.5" /> <span className="max-w-[160px] truncate">{d.original_filename}</span>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </li>
  );
}
